import { Fragment, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { ContactForm } from './ContactForm';
import { LegalBlock, LegalDocument, legalDocuments, services, site } from './content';
import { Breadcrumbs, CTASection, PageIntro, PageLayout, PricingNotice, formatDate } from './Shell';

function ServiceCards({ limit }: { limit?: number }) {
  return <div className="card-grid service-grid">{services.slice(0, limit).map((service, index) => <article className="card service-card" key={service.id}><a className="service-card-link" href={`/services#${service.id}`}><span className="card-index">0{index + 1}</span><h3>{service.title}</h3><p>{service.objective}</p><strong>{service.price.display}</strong><span className="service-card-action">Подробнее об услуге <span aria-hidden="true">→</span></span></a></article>)}</div>;
}

function PriceList({ compact = false }: { compact?: boolean }) {
  return <div className={`price-card-list${compact ? ' compact' : ''}`}>{services.map((service) => <article className="price-card" key={service.id}><a className="price-card-link" href={`/services#${service.id}`}><h3>{service.title}</h3><p className="price">{service.price.display}</p>{!compact && <p>{service.price.unit}</p>}</a></article>)}</div>;
}

export function fragmentFocusTarget(target: HTMLElement): HTMLElement {
  return target.querySelector<HTMLElement>('h1[tabindex], h2[tabindex], h3[tabindex], h4[tabindex], h5[tabindex], h6[tabindex]') ?? target;
}

export function activateFragmentTarget(target: HTMLElement, scrollRoot = document.documentElement) {
  const previousScrollBehavior = scrollRoot.style.scrollBehavior;
  scrollRoot.style.scrollBehavior = 'auto';
  try {
    target.scrollIntoView({ block: 'start' });
  } finally {
    scrollRoot.style.scrollBehavior = previousScrollBehavior;
  }
  focusFragmentTarget(target);
}

function focusFragmentTarget(target: HTMLElement) {
  const focusTarget = fragmentFocusTarget(target);
  focusTarget.dataset.fragmentFocus = 'true';
  focusTarget.addEventListener('blur', () => { delete focusTarget.dataset.fragmentFocus; }, { once: true });
  focusTarget.focus({ preventScroll: true });
}

function useFragmentTarget(ids: readonly string[]) {
  useEffect(() => {
    let frame: number | null = null;
    const focusTarget = () => {
      let id = '';
      try {
        id = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }
      if (!ids.includes(id)) return;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        const target = document.getElementById(id);
        if (target) activateFragmentTarget(target);
      });
    };
    focusTarget();
    window.addEventListener('hashchange', focusTarget);
    window.addEventListener('popstate', focusTarget);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', focusTarget);
      window.removeEventListener('popstate', focusTarget);
    };
  }, [ids]);
}

const companySections = [
  ['facts', 'Факты'],
  ['activities', 'ИТ-деятельность'],
  ['offer', 'Услуги и цены'],
  ['actuality', 'Актуальность'],
] as const;

type CompanySectionId = (typeof companySections)[number][0];
export const companyInitialSection: CompanySectionId = 'facts';

export function companySectionFromHash(hash: string): CompanySectionId | null {
  const id = hash.slice(1);
  return companySections.some(([candidate]) => candidate === id) ? id as CompanySectionId : null;
}

function CompanySubnav({ onNavigate }: { onNavigate: (id: CompanySectionId) => void }) {
  // Do not read location during the initial client render: it must be identical
  // to SSR. The effect below applies a direct fragment after hydration.
  const [active, setActive] = useState<CompanySectionId>(companyInitialSection);
  const activeRef = useRef(active);

  useEffect(() => {
    const setLocation = (id: CompanySectionId, replace = false) => {
      activeRef.current = id;
      setActive(id);
      if (replace && window.location.hash !== `#${id}`) window.history.replaceState(null, '', `#${id}`);
    };
    const syncFromHistory = () => {
      const id = companySectionFromHash(window.location.hash);
      if (id) setLocation(id);
    };
    window.addEventListener('hashchange', syncFromHistory);
    window.addEventListener('popstate', syncFromHistory);
    syncFromHistory();
    const targets = companySections.map(([id]) => document.getElementById(id)).filter((target): target is HTMLElement => Boolean(target));
    if (!targets.length || typeof IntersectionObserver === 'undefined') return () => {
      window.removeEventListener('hashchange', syncFromHistory);
      window.removeEventListener('popstate', syncFromHistory);
    };
    const observer = new IntersectionObserver((entries) => {
      const candidate = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      const id = candidate && companySectionFromHash(`#${candidate.target.id}`);
      if (id && id !== activeRef.current) setLocation(id, true);
    }, { threshold: 0.25 });
    targets.forEach((target) => observer.observe(target));
    return () => {
      observer.disconnect();
      window.removeEventListener('hashchange', syncFromHistory);
      window.removeEventListener('popstate', syncFromHistory);
    };
  }, []);

  return <nav className="company-subnav" aria-label="Разделы страницы о компании"><div className="container company-subnav-track">{companySections.map(([id, label]) => <a href={`#${id}`} aria-current={active === id ? 'location' : undefined} key={id} onClick={(event) => {
    event.preventDefault();
    activeRef.current = id;
    setActive(id);
    onNavigate(id);
  }}>{label}</a>)}</div></nav>;
}

function ProjectCards() {
  return <div className="project-grid">{site.projects.map((project) => <article className="card project-card" key={project.id}><h2>{project.name}</h2><p className="project-description">{project.description}</p><a className="text-link external" href={project.url} target="_blank" rel="noreferrer">Перейти на сайт {project.id === 'ruant' ? 'РУАНТ' : 'НАУТОР'} <span aria-hidden="true">↗</span><span className="visually-hidden"> (откроется в новой вкладке)</span></a></article>)}</div>;
}

export function nextHeroMotionFrame(step: number): { nextStep: number; delayMs: number } {
  return { nextStep: (step + 1) % 5, delayMs: 750 };
}

export function createHeroMotionLoop({ schedule, cancel, canRun, onStep }: { schedule: (callback: () => void, delayMs: number) => number; cancel: (timer: number) => void; canRun: () => boolean; onStep: (step: number) => void }) {
  let timer: number | null = null;
  let step = 0;
  const scheduleNext = () => {
    if (!canRun() || timer !== null) return;
    const frame = nextHeroMotionFrame(step);
    timer = schedule(() => {
      timer = null;
      if (!canRun()) return;
      step = frame.nextStep;
      onStep(step);
      scheduleNext();
    }, frame.delayMs);
  };
  return {
    start() {
      if (!canRun() || timer !== null) return;
      step = 0;
      onStep(step);
      scheduleNext();
    },
    stop() {
      if (timer !== null) cancel(timer);
      timer = null;
    },
  };
}

function isInitiallyVisible(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
  return rect.width > 0 && rect.height > 0 && visibleHeight / rect.height >= 0.25;
}

function useViewportLoops() {
  const hero = useRef<HTMLElement>(null);
  const process = useRef<HTMLOListElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const heroTarget = { element: hero.current, visible: false };
    const processTarget = { element: process.current, timer: null as number | null, visible: false };
    const heroLoop = createHeroMotionLoop({
      schedule: (callback, delayMs) => window.setTimeout(callback, delayMs),
      cancel: (timer) => window.clearTimeout(timer),
      canRun: () => !reducedMotion.matches && heroTarget.visible && Boolean(heroTarget.element),
      onStep: (step) => { if (heroTarget.element) heroTarget.element.dataset.motionStep = String(step); },
    });

    const stopHero = () => {
      heroLoop.stop();
      if (heroTarget.element) delete heroTarget.element.dataset.motionStep;
    };
    const startHero = () => heroLoop.start();
    const stopProcess = () => {
      if (processTarget.timer !== null) window.clearInterval(processTarget.timer);
      processTarget.timer = null;
      if (processTarget.element) delete processTarget.element.dataset.motionStep;
    };
    const startProcess = () => {
      if (reducedMotion.matches || !processTarget.visible || !processTarget.element || processTarget.timer !== null) return;
      let step = 0;
      processTarget.element.dataset.motionStep = String(step);
      processTarget.timer = window.setInterval(() => {
        step = (step + 1) % 3;
        if (processTarget.element) processTarget.element.dataset.motionStep = String(step);
      }, 1300);
    };
    const sync = () => {
      stopHero();
      stopProcess();
      startHero();
      startProcess();
    };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === heroTarget.element) {
          if (heroTarget.visible === entry.isIntersecting) continue;
          heroTarget.visible = entry.isIntersecting;
          if (heroTarget.visible) startHero(); else stopHero();
        } else if (entry.target === processTarget.element) {
          if (processTarget.visible === entry.isIntersecting) continue;
          processTarget.visible = entry.isIntersecting;
          if (processTarget.visible) startProcess(); else stopProcess();
        }
      }
    }, { threshold: 0.25 });
    if (heroTarget.element) {
      observer.observe(heroTarget.element);
      heroTarget.visible = isInitiallyVisible(heroTarget.element);
      startHero();
    }
    if (processTarget.element) {
      observer.observe(processTarget.element);
      processTarget.visible = isInitiallyVisible(processTarget.element);
      startProcess();
    }
    reducedMotion.addEventListener('change', sync);
    return () => {
      observer.disconnect();
      reducedMotion.removeEventListener('change', sync);
      stopHero();
      stopProcess();
    };
  }, []);

  return { hero, process };
}

function HeroContour({ motionRef }: { motionRef: RefObject<HTMLElement | null> }) {
  const stages = [['01', 'Исследование', 'Задача, процессы и требования'], ['02', 'Проектирование', 'Сценарии, структура и границы'], ['03', 'Разработка', 'Работающий цифровой продукт'], ['04', 'Развитие', 'Модернизация и сопровождение']];
  return <aside ref={motionRef} className="solution-map" aria-label="Контур цифрового решения" data-motion-contour>
    <p className="map-label">Контур цифрового решения</p>
    <div className="map-body">
      <svg className="contour-geometry" width="52" height="342" viewBox="0 0 52 342" aria-hidden="true">
        <path className="contour-route" pathLength="1" d="M17 51V291 M17 51H52 M17 131H52 M17 211H52 M17 291H52" />
        {[51, 131, 211, 291].map((y, index) => <g className={`contour-node contour-node-${index + 1}`} key={y}><circle cx="17" cy={y} r="17" /><text x="17" y={y} textAnchor="middle" dominantBaseline="central">0{index + 1}</text></g>)}
      </svg>
      <div className="contour-stages">{stages.map(([number, title, copy]) => <div className="contour-stage" key={number}><h2>{title}</h2><p>{copy}</p></div>)}</div>
    </div>
    <div className="map-result"><span className="result-mark" aria-hidden="true">›<span className="result-cursor">_</span></span><p>Связываем этапы вокруг одной согласованной задачи.</p></div>
  </aside>;
}

export function HomePage() {
  const { hero, process } = useViewportLoops();
  return <PageLayout path="/">
    <section className="hero"><div className="container hero-grid"><div><p className="eyebrow">Заказная разработка для бизнеса</p><h1>Цифровые решения для задач вашего бизнеса</h1><p className="lead">Исследуем контекст, проектируем интерфейсы, разрабатываем сайты и программные продукты, модернизируем и сопровождаем работающие системы.</p><div className="button-row"><a className="button primary" href="/contacts#request">Обсудить задачу</a><a className="button inverse-button" href="/prices">Посмотреть цены</a></div></div><HeroContour motionRef={hero} /></div></section>
    <section className="section"><div className="container"><div className="section-heading"><div><h2>Рабочий результат — в центре каждой услуги</h2></div><a className="text-link" href="/services">Все услуги <span aria-hidden="true">→</span></a></div><ServiceCards /></div></section>
    <section className="section tinted"><div className="container"><div className="section-heading"><div><h2>Клиенты и проекты</h2><p className="lead">Решения для конкретных рабочих задач.</p></div><a className="text-link" href="/projects">Все проекты <span aria-hidden="true">→</span></a></div><ProjectCards /></div></section>
    <HomeTechnologies />
    <section className="section approach-section"><div className="container approach-grid"><div className="approach-intro"><p className="eyebrow">Как начинается работа</p><h2>Понятный путь от сообщения к договорённостям</h2><p>Начинаем с контекста задачи и последовательно уточняем результат, который нужен бизнесу.</p><div className="response-card"><strong>1 рабочий день</strong><span>на первый ответ</span></div></div><ol ref={process} className="approach-steps" data-motion-process><li><span className="approach-indicator" aria-hidden="true" /><span className="approach-number">01</span><div><h3>Опишите контекст</h3><p>Кратко расскажите о задаче и оставьте удобный способ связи.</p></div></li><li><span className="approach-indicator" aria-hidden="true" /><span className="approach-number">02</span><div><h3>Получите первый ответ</h3><p>Вернёмся в течение одного рабочего дня, чтобы договориться о разговоре.</p></div></li><li><span className="approach-indicator" aria-hidden="true" /><span className="approach-number">03</span><div><h3>Зафиксируйте результат</h3><p>После обследования состав работ и итоговая стоимость закрепляются в договоре.</p></div></li></ol></div></section>
    <CTASection />
  </PageLayout>;
}

export function ServicesPage() {
  useFragmentTarget(services.map(({ id }) => id));
  return <PageLayout path="/services"><div className="container"><Breadcrumbs path="/services" /></div><PageIntro compact title="Услуги" lead="Шесть направлений: от обследования задачи до сопровождения работающего программного контура." /><section className="section no-top"><div className="container"><nav className="anchor-nav" aria-label="Услуги на странице">{services.map((service) => <a href={`/services#${service.id}`} key={service.id}>{service.title}</a>)}</nav><div className="service-details">{services.map((service, index) => <article id={service.id} key={service.id} className="service-detail"><div className="detail-heading"><span className="card-index">0{index + 1}</span><div><h2 tabIndex={-1}>{service.title}</h2><p>{service.objective}</p></div><p className="price">{service.price.display}</p></div><div className="detail-grid"><div><h3>Результат</h3><p>{service.result}</p><h3>Формат</h3><p>{service.format}</p></div><div><h3>Что входит</h3><ul>{service.included.map((item) => <li key={item}>{item}</li>)}</ul><h3>Что не входит</h3><ul>{service.excluded.map((item) => <li key={item}>{item}</li>)}</ul></div><div><h3>Что влияет на оценку</h3><ul>{service.estimateFactors.map((item) => <li key={item}>{item}</li>)}</ul><a className="button secondary" href="/contacts#request">Обсудить задачу</a></div></div></article>)}</div><PricingNotice /></div></section><CTASection /></PageLayout>;
}

export function PricesPage() {
  useFragmentTarget(services.map(({ id }) => id));
  return <PageLayout path="/prices"><div className="container"><Breadcrumbs path="/prices" /></div><PageIntro compact title="Цены" lead="Минимальные пороги коммерческого предложения и существенные условия оценки." /><section className="section no-top"><div className="container"><div className="table-wrap"><table className="price-table"><caption>Минимальная стоимость услуг Sparksoft</caption><thead><tr><th scope="col">Услуга</th><th scope="col">Единица</th><th scope="col">Минимальная стоимость</th></tr></thead><tbody>{services.map((service) => <tr id={service.id} tabIndex={-1} key={service.id}><th scope="row"><a href={`/services#${service.id}`}>{service.title}</a></th><td><span className="price-cell-label">Единица</span>{service.price.unit}</td><td><span className="price-cell-label">Минимальная стоимость</span>{service.price.display}</td></tr>)}</tbody></table></div><PricingNotice /><p className="actuality-date">Сведения актуальны на {formatDate(site.lastReviewedAt)}.</p><div className="estimate-grid">{services.map((service) => <article className="card estimate-card" key={service.id}><div className="estimate-card-head"><h2><a href={`/services#${service.id}`}>{service.title}</a></h2><EstimateVisual kind={service.id} /></div><p><strong>Параметры оценки:</strong></p><ul>{service.estimateFactors.map((factor) => <li key={factor}>{factor}</li>)}</ul></article>)}</div></div></section><CTASection /></PageLayout>;
}

function EstimateVisual({ kind }: { kind: string }) {
  return <svg className={`estimate-visual estimate-visual-${kind}`} viewBox="0 0 160 112" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id={`estimate-red-${kind}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ff4b4b" />
        <stop offset="1" stopColor="#b80000" />
      </linearGradient>
      <linearGradient id={`estimate-gray-${kind}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#f2f4f3" />
        <stop offset="1" stopColor="#d8ddda" />
      </linearGradient>
    </defs>
    <circle className="estimate-visual-glow" cx="112" cy="45" r="50" />
    <g className="estimate-visual-motion">
      {kind === 'analysis' && <>
        <path className="estimate-visual-grid" d="M18 91H145M27 81V35M53 81V49M79 81V28M105 81V43M131 81V20" />
        <path className="estimate-visual-fill" d="M27 81V61h14v20zM53 81V47h14v34zM79 81V55h14v26zM105 81V34h14v47z" />
        <path className="estimate-visual-accent" d="m28 58 31-20 27 10 38-27" />
        <circle className="estimate-visual-dot" cx="124" cy="21" r="7" />
        <circle cx="53" cy="40" r="23" />
        <path d="m70 57 17 17" />
      </>}
      {kind === 'design' && <>
        <rect className="estimate-visual-panel back" x="43" y="17" width="89" height="68" rx="12" />
        <rect className="estimate-visual-panel" x="23" y="30" width="96" height="67" rx="12" />
        <path className="estimate-visual-grid" d="M23 47h96M45 47v50M45 65h74M45 81h74" />
        <circle className="estimate-visual-dot" cx="33" cy="39" r="3" />
        <path className="estimate-visual-accent" d="m100 18 23-7-7 23-5-9-11-7Z" />
        <path d="M60 61h23M60 76h39M60 88h27" />
      </>}
      {kind === 'web' && <>
        <rect className="estimate-visual-panel" x="19" y="20" width="122" height="76" rx="12" />
        <path className="estimate-visual-grid" d="M19 39h122" />
        <circle className="estimate-visual-dot" cx="30" cy="30" r="3" />
        <circle cx="40" cy="30" r="3" />
        <circle cx="50" cy="30" r="3" />
        <rect className="estimate-visual-fill" x="30" y="51" width="47" height="33" rx="7" />
        <path d="M87 52h39M87 63h31M87 74h37M87 85h22" />
        <path className="estimate-visual-accent" d="m67 57 18 15-10 2-5 10-3-27Z" />
      </>}
      {kind === 'software' && <>
        <rect className="estimate-visual-panel" x="16" y="16" width="128" height="82" rx="13" />
        <path className="estimate-visual-grid" d="M16 36h128" />
        <circle className="estimate-visual-dot" cx="28" cy="26" r="3" />
        <path d="m48 52-16 13 16 13M112 52l16 13-16 13M91 45 69 86" />
        <path className="estimate-visual-accent" d="M60 48h25M60 59h18M94 81h22" />
        <rect className="estimate-visual-chip" x="88" y="50" width="19" height="19" rx="5" />
      </>}
      {kind === 'migration' && <>
        <ellipse className="estimate-visual-panel" cx="43" cy="34" rx="27" ry="12" />
        <path className="estimate-visual-panel" d="M16 34v42c0 7 12 12 27 12s27-5 27-12V34M16 54c0 7 12 12 27 12s27-5 27-12" />
        <ellipse className="estimate-visual-panel back" cx="118" cy="57" rx="25" ry="11" />
        <path className="estimate-visual-panel back" d="M93 57v25c0 6 11 11 25 11s25-5 25-11V57" />
        <path className="estimate-visual-accent" d="M73 35h54l-12-12M127 35l-12 12" />
        <circle className="estimate-visual-dot" cx="82" cy="35" r="5" />
      </>}
      {kind === 'support' && <>
        <circle className="estimate-visual-panel" cx="79" cy="56" r="35" />
        <circle cx="79" cy="56" r="15" />
        <path d="M79 11v10M79 91v10M34 56h10M114 56h10M47 24l8 8M103 80l8 8M111 24l-8 8M55 80l-8 8" />
        <path className="estimate-visual-accent" d="M79 42v15l12 8M19 38c9-24 33-36 56-31M139 74c-9 24-33 36-56 31" />
        <circle className="estimate-visual-dot" cx="22" cy="37" r="6" />
        <circle className="estimate-visual-dot secondary" cx="136" cy="75" r="6" />
      </>}
    </g>
  </svg>;
}

export function ProjectsPage() {
  return <PageLayout path="/projects"><div className="container"><Breadcrumbs path="/projects" /></div><PageIntro compact title="Клиенты и проекты" lead="Два примера задач: автоматизация внутреннего процесса и обновление цифровой инфраструктуры." /><section className="section no-top"><div className="container"><ProjectCards /></div></section><CTASection /></PageLayout>;
}

function TechnologyMarquee({ items, direction, label }: { items: string[]; direction: 'forward' | 'reverse'; label: string }) {
  const renderItems = (duplicate = false) => <ul className="technology-list" aria-hidden={duplicate || undefined}>{items.map((item) => <li key={item}>{item}<span aria-hidden="true">·</span></li>)}</ul>;
  return <a className={`technology-marquee ${direction}`} href="/company#technologies" aria-label={`${label}: перейти к разделу технологий`}><div className="technology-track">{renderItems()}{renderItems(true)}</div></a>;
}

function HomeTechnologies() {
  return <section className="home-technologies" aria-labelledby="home-technologies-title"><div className="container"><h2 id="home-technologies-title">{site.technologies.heading}</h2><p>{site.technologies.description}</p></div><div className="technology-marquees"><TechnologyMarquee items={site.technologies.rows[0]} direction="forward" label="Языки и фреймворки" /><TechnologyMarquee items={site.technologies.rows[1]} direction="reverse" label="Данные и инфраструктурные инструменты" /></div></section>;
}

const yandexMapUrl = 'https://yandex.ru/map-widget/v1/?ll=37.525783%2C55.739174&mode=whatshere&whatshere%5Bpoint%5D=37.525783%2C55.739174&whatshere%5Bzoom%5D=17&z=17';

function ContactMap({ address, mapUrl }: { address: string; mapUrl?: string }) {
  const [requested, setRequested] = useState(false);
  const canEmbed = mapUrl === yandexMapUrl;

  return <section className="contact-map" aria-labelledby="contact-map-title">
    <h2 id="contact-map-title">Офис</h2>
    <p><strong>Адрес офиса:</strong><br />{address}</p>
    {!requested && <button className="button secondary compact" type="button" onClick={() => setRequested(true)}>Показать карту</button>}
    {requested && canEmbed && <iframe className="contact-map-frame" title={`Карта: ${address}`} src={mapUrl} loading="lazy" referrerPolicy="no-referrer" />}
    {requested && !canEmbed && <p className="hint" role="status">Карта сейчас недоступна. Откройте адрес во внешней карте по ссылке ниже.</p>}
    {!requested && <p className="hint">Карта загрузится только после нажатия кнопки.</p>}
    {canEmbed && <p className="hint"><a href={mapUrl} target="_blank" rel="noreferrer">Открыть карту в новой вкладке</a><span className="visually-hidden"> (откроется в новой вкладке)</span></p>}
  </section>;
}

export function CompanyPage() {
  const organization = site.organization;
  const pendingFocus = useRef<number | null>(null);
  useFragmentTarget([...companySections.map(([id]) => id), 'technologies']);

  useEffect(() => {
    return () => { if (pendingFocus.current !== null) window.clearTimeout(pendingFocus.current); };
  }, []);

  const navigateToSection = (id: CompanySectionId) => {
    if (pendingFocus.current !== null) window.clearTimeout(pendingFocus.current);
    const target = document.getElementById(id);
    if (!target) return;
    window.history.pushState(null, '', `#${id}`);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      activateFragmentTarget(target);
      return;
    }

    target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    pendingFocus.current = window.setTimeout(() => {
      pendingFocus.current = null;
      focusFragmentTarget(target);
    }, 560);
  };

  return <PageLayout path="/company"><div className="container"><Breadcrumbs path="/company" /></div><PageIntro compact title="О компании" lead="Сведения о компании, её контактах и работе." /><CompanySubnav onNavigate={navigateToSection} /><section className="section no-top"><div className="container company-content"><section id="facts" tabIndex={-1}><h2>Юридические сведения</h2><dl className="company-facts"><div><dt>Полное наименование</dt><dd>{organization.fullName}</dd></div><div><dt>Адрес места нахождения</dt><dd>{organization.address}</dd></div><div><dt>Фактический офис</dt><dd>{organization.officeAddress}</dd></div><div><dt>ИНН</dt><dd>{organization.inn}</dd></div><div><dt>ОГРН</dt><dd>{organization.ogrn}</dd></div><div><dt>КПП</dt><dd>{organization.kpp}</dd></div><div><dt>Основной ОКВЭД</dt><dd>{organization.okved}</dd></div><div><dt>Публичный email</dt><dd><a href={`mailto:${organization.email}`}>{organization.email}</a></dd></div></dl></section><section id="activities" tabIndex={-1}><h2>Виды ИТ-деятельности</h2><p>Коды и официальные наименования приведены по перечню, утвержденному приказом Минцифры России от 11.05.2023 № 449. Рядом указано фактическое описание работ Sparksoft в границах утвержденного каталога.</p><div className="activity-list">{site.activities.map((activity) => <article key={activity.code}><span className="activity-code">{activity.code}</span><div><h3>{activity.officialName}</h3><p>{activity.actualDescription}</p></div></article>)}</div></section><section id="technologies" tabIndex={-1}><h2>{site.technologies.heading}</h2><p>{site.technologies.description}</p><ul className="technology-static-list">{site.technologies.rows.flatMap((items) => items).map((item) => <li key={item}>{item}</li>)}</ul></section><section id="offer" tabIndex={-1}><h2>Услуги и стоимость</h2><p>Sparksoft оказывает шесть видов проектных и регулярных услуг. Полный состав результата и границы опубликованы в <a href="/services">каталоге услуг</a>, минимальные цены и существенные условия — в <a href="/prices">ценах</a>.</p><PriceList compact /><PricingNotice /></section><section id="actuality" tabIndex={-1}><h2>Актуальность сведений</h2><p className="actuality-date">Сведения актуальны на {formatDate(site.lastReviewedAt)}.</p></section></div></section><CTASection /></PageLayout>;
}

export function ContactsPage() {
  const organization = site.organization;
  return <PageLayout path="/contacts"><div className="container"><Breadcrumbs path="/contacts" /></div><section className="section no-top"><div className="container contacts-layout"><div className="contact-info"><h2>Публичный контакт</h2><a className="email-link" href={`mailto:${organization.email}`}>{organization.email}</a><p>Первый ответ — в течение одного рабочего дня.</p><hr /><ContactMap address={organization.officeAddress} mapUrl={site.mapEmbed?.yandexMapUrl} /><hr /><h2>Реквизиты</h2><p>{organization.legalFormName}<br />ИНН {organization.inn}<br />ОГРН {organization.ogrn}</p><p><strong>Адрес места нахождения:</strong><br />{organization.address}</p><p><strong>Фактический офис:</strong><br />{organization.officeAddress}</p><hr /><h2>Если форма недоступна</h2><p>Напишите на <a href={`mailto:${organization.email}`}>{organization.email}</a>.</p></div><ContactForm /></div></section></PageLayout>;
}

function Inline({ text = '' }: { text?: string }) {
  const nodes: ReactNode[] = [];
  const pattern = /\[([^\]]+)]\(([^)]+)\)/g;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    nodes.push(text.slice(last, index));
    nodes.push(<a key={`${match[2]}-${index}`} href={match[2]}>{match[1]}</a>);
    last = index + match[0].length;
  }
  nodes.push(text.slice(last));
  return <>{nodes}</>;
}

function LegalBlockView({ block }: { block: LegalBlock }) {
  if (block.type === 'heading') {
    if (block.level === 3) return <h3 id={block.id}><Inline text={block.text} /></h3>;
    return <h2 id={block.id}><Inline text={block.text} /></h2>;
  }
  if (block.type === 'list') return <ul>{block.items?.map((item, index) => <li key={index}><Inline text={item} /></li>)}</ul>;
  return <p><Inline text={block.text} /></p>;
}

function LegalToc({ headings }: { headings: { id: string; text: string }[] }) {
  const [active, setActive] = useState(headings[0]?.id ?? '');
  const activeRef = useRef(active);

  useEffect(() => {
    let frame: number | null = null;
    const syncActiveHeading = () => {
      frame = null;
      let next = headings[0]?.id ?? '';
      const threshold = 132;
      for (const heading of headings) {
        const target = document.getElementById(heading.id);
        if (target && target.getBoundingClientRect().top <= threshold) next = heading.id;
        else break;
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) next = headings.at(-1)?.id ?? next;
      if (next && next !== activeRef.current) {
        activeRef.current = next;
        setActive(next);
      }
    };
    const scheduleSync = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(syncActiveHeading);
    };
    scheduleSync();
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync);
    window.addEventListener('hashchange', scheduleSync);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('hashchange', scheduleSync);
    };
  }, [headings]);

  return <nav className="page-toc" aria-label="Оглавление документа"><strong>Содержание</strong>{headings.map((heading) => <a key={heading.id} href={`#${heading.id}`} aria-current={active === heading.id ? 'location' : undefined} onClick={() => {
    activeRef.current = heading.id;
    setActive(heading.id);
  }}>{heading.text}</a>)}</nav>;
}

export function LegalPage({ document }: { document: LegalDocument }) {
  const path = `/legal/${document.slug}`;
  const headings = document.blocks.flatMap((block) => block.type === 'heading' && block.level === 2 && block.id && block.text ? [{ id: block.id, text: block.text }] : []);
  return <PageLayout path={path}><div className="container"><Breadcrumbs path={path} /></div><div className="legal-page-intro"><PageIntro compact title={document.title} lead="Правовой документ ООО «СпаркСофт»." /></div><section className="section no-top legal-section"><div className="container legal-layout"><aside className="legal-toc-column"><LegalToc headings={headings} /></aside><article className="legal-article">{document.blocks.map((block, index) => <Fragment key={`${block.id || block.type}-${index}`}><LegalBlockView block={block} /></Fragment>)}<div className="revision-card"><strong>Дата редакции</strong><span>{formatDate(document.effectiveDate)}</span></div></article></div></section></PageLayout>;
}

export function NotFoundPage({ path = '/404' }: { path?: string }) {
  return <PageLayout path={path}><PageIntro eyebrow="Ошибка 404" title="Страница не найдена" lead="Такого адреса нет или страница была перемещена." /><section className="section no-top"><div className="container narrow"><div className="button-row"><a className="button primary" href="/">На главную</a><a className="button secondary" href="/services">Услуги</a><a className="button secondary" href="/prices">Цены</a><a className="button secondary" href="/contacts">Контакты</a></div></div></section></PageLayout>;
}

export const PrivacyPage = () => <LegalPage document={legalDocuments.privacy} />;
export const ConsentPage = () => <LegalPage document={legalDocuments.consent} />;
export const CookiesPage = () => <LegalPage document={legalDocuments.cookies} />;
